<template>
  <Breadcrumb></Breadcrumb>
  <div class="px-4 sm:px-0" v-if="company">

    <div class="grid grid-cols-3">
      <div class="col-span-2">
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow m-4">
          <div class="px-4 py-5 sm:p-6">
            <div class="flex gap-2">
              <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <BuildingOfficeIcon class="h-8 w-8 text-gray-700"></BuildingOfficeIcon>
              </div>
              <div class="p-2">
                <p class="text-m font-semibold">{{ company.attributes.name }}</p>
              </div>
              <div class="p-2">
                <StatusLabel v-if="riskScoring" :riskScoring="riskScoring" />
              </div>
              <div class="p-2">
                <RiskScoringLabel v-if="riskScoring" :level="riskScoring.attributes.scoreLevel ?? undefined" />
              </div>
              <router-link class="p-2 text-indigo-600 hover:text-indigo-900 ml-auto transition-transform duration-200 hover:scale-110" :to="{ name: 'edit-company', params: { id: company.id } }">
                <PencilSquareIcon class="size-5" aria-hidden="true" />
              </router-link>
            </div>
          </div>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow m-4">
          <RiskScoringFactorList v-if="riskScoring" :riskScoring="riskScoring" :riskScoringStore="riskScoringStore" @updated="handleRiskScoringUpdate"/>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow m-4">
          <div class="px-4 py-5 sm:px-6">
            <SaleListingAgreementList :saleListingAgreements="company.relationships?.saleListingAgreements.data"></SaleListingAgreementList>
          </div>
          <div class="px-4 py-5 sm:px-6">
            <RentalListingAgreementList :rentalListingAgreements="company.relationships?.rentalListingAgreements.data"></RentalListingAgreementList>
          </div>
        </div>
      </div>
      <div class="col-span-1">
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6">
            <span class="text-xl font-semibold text-gray-900">{{ $gettext("Aperçu") }}</span>
          </div>
          <RiskScoringChart v-if="riskScoring" :riskScoring="riskScoring" />
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6" v-if="riskScoring">
            <SanctionsBox v-if="riskScoring" :riskScoring="riskScoring" />
          </div>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6" v-if="company.relationships.companyDocuments.data">
            <DocumentList v-if="company.relationships.companyDocuments.data" :documents="company.relationships.companyDocuments.data" />
          </div>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6">
              <span class="text-xl font-semibold text-gray-900">{{ $gettext("Liens officiels") }}</span>
              <div class="mt-4">
                <KboLink v-if="company.attributes.companyNumber" :enterpriseNumber="company.attributes.companyNumber" />
                <EjusticeLink v-if="company.attributes.companyNumber" :enterpriseNumber="company.attributes.companyNumber" />
                <FednotLink v-if="company.attributes.companyNumber" :enterpriseNumber="company.attributes.companyNumber" />
                <NbbLink v-if="company.attributes.companyNumber" :enterpriseNumber="company.attributes.companyNumber" />
              </div>
            </div>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6">
            <ReportList :reports="companyReportStore.all" report-type="company" @build="onBuildReport" @delete="onDeleteReport" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from "vue";
import { ref } from "vue";
import { useCompanyStore } from "@/stores/company.js";
import { useRiskScoring } from "@/stores/riskScoring";
import { useRoute } from "vue-router";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import StatusLabel from "@/components/layout/StatusLabel.vue";
import RiskScoringFactorList from "@/components/risk-scorings/RiskScoringFactorList.vue";
import { BuildingOfficeIcon } from "@heroicons/vue/24/outline";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import { PencilSquareIcon } from "@heroicons/vue/24/solid";
import KboLink from "@/components/companies/links/KboLink.vue"
import FednotLink from "@/components/companies/links/FednotLink.vue"
import EjusticeLink from "@/components/companies/links/EjusticeLink.vue"
import NbbLink from "@/components/companies/links/NbbLink.vue"


import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import Breadcrumb from "@/components/layout/BreadcrumbNavigation.vue";
import RiskScoringChart from "@/components/risk-scorings/RiskScoringChart.vue";
import SanctionsBox from "@/components/risk-scorings/SanctionsBox.vue";
import SaleListingAgreementList from "@/components/sale-listing-agreements/SaleListingAgreementList.vue";
import RentalListingAgreementList from "@/components/rental-listing-agreements/RentalListingAgreementList.vue";
import ReportList from "@/components/reports/ReportList.vue";
import DocumentList from "@/components/documents/DocumentList.vue";
import { useCompanyReportStore } from "@/stores/companyReport";

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

const route = useRoute();
const companyId = route.params.id;
const company = ref(null);
const companyStore = useCompanyStore();
const companyReportStore = useCompanyReportStore(companyId);
const riskScoringStore = useRiskScoring("companies", companyId);
const riskScoring = ref(null);
const companyIncludes = ["companyDocuments", "saleListingAgreements", "rentalListingAgreements", "lastRiskScoring"];

const onBuildReport = async () => {
  await companyStore.buildReport(companyId, () => {
    companyReportStore.fetchAll();
  }, null, companyIncludes);
};

const onDeleteReport = async (report) => {
  await companyStore.deleteReport(report.id, companyId, () => {
    companyReportStore.fetchAll();
  });
};

const loadCompany = async () => {
  company.value = await companyStore.fetchCompany(companyId, true, null, companyIncludes);
};

const handleRiskScoringUpdate = async () => {
  await loadCompany();
  let riskScoringId = company.value.relationships.lastRiskScoring.data.id
  riskScoring.value = await riskScoringStore.fetchOne(riskScoringId);
};

onMounted(async () => {
  await loadCompany();
  let riskScoringId = company.value.relationships.lastRiskScoring.data.id
  riskScoring.value = await riskScoringStore.fetchOne(riskScoringId);
  await companyReportStore.fetchAll();
  await companyReportStore.watchResourceChanges(() => {
    companyReportStore.fetchAll();
  });
});

</script>
